import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Briefcase, Gavel, Truck, CheckCircle2, XCircle, Clock, MapPin, ChevronRight, Sparkles, AlertCircle, X, TrendingDown, Hourglass, Ban, Search, Trash2, ArrowRight, MessageSquare } from 'lucide-react';
import { useActivity, cancelJob, acceptBid, rejectBid, bidsWithLifecycle, clearSearches, statusLabel, statusColor, isCancellable, JobRecord, JobStatus, JobBid, BidStatus, SearchRecord, OfferRecord, OfferStatus, acceptOffer, rejectOffer, submitUserCounter, markOfferRead } from '../lib/activityStore';
import { setActiveService, detectServiceFromPrompt } from '../lib/serviceData';
import { RateJobModal } from '../components/RateJobModal';
import { ReceiptModal } from '../components/ReceiptModal';
import { Star } from 'lucide-react';
type Tab = 'all' | 'offers' | 'bidding' | 'active' | 'completed' | 'cancelled' | 'searches';
const TABS: {
  id: Tab;
  label: string;
  icon: ComponentType<any>;
}[] = [{
  id: 'all',
  label: 'Jobs',
  icon: Briefcase
}, {
  id: 'offers',
  label: 'Offers',
  icon: TrendingDown
}, {
  id: 'bidding',
  label: 'Bidding',
  icon: Gavel
}, {
  id: 'searches',
  label: 'Searches',
  icon: Search
}];
export function Activity() {
  const {
    jobs,
    searches,
    offers
  } = useActivity();
  const navigate = useNavigate();
  const [offerDetail, setOfferDetail] = useState<OfferRecord | null>(null);
  const [rateTarget, setRateTarget] = useState<JobRecord | null>(null);
  const [receiptTarget, setReceiptTarget] = useState<JobRecord | null>(null);
  // The most recent completed-but-unrated job — surfaced as a prompt banner.
  const unratedJob = useMemo(() => jobs.filter(j => j.status === 'completed' && !j.rating).sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))[0], [jobs]);
  const location = useLocation();
  // Respect a hinted initial tab (e.g. coming from "send offer" flow).
  const initialTab = (location.state as {
    initialTab?: Tab;
  } | null)?.initialTab;
  const [tab, setTab] = useState<Tab>(initialTab || 'all');
  const [cancelTarget, setCancelTarget] = useState<JobRecord | null>(null);
  const [bidDetailTarget, setBidDetailTarget] = useState<JobRecord | null>(null);
  // Keep the modal target in sync with the latest store data.
  const detailJob = useMemo(() => {
    if (!bidDetailTarget) return null;
    return jobs.find(j => j.id === bidDetailTarget.id) || bidDetailTarget;
  }, [bidDetailTarget, jobs]);
  // Per-tab sub-filter (e.g. 'pending'/'accepted' within Bidding).
  // Defaults to 'all' for every tab.
  const [subFilters, setSubFilters] = useState<Record<string, string>>({});
  const subFilter = subFilters[tab] || 'all';
  const setSubFilter = (val: string) => setSubFilters(m => ({
    ...m,
    [tab]: val
  }));
  const baseFiltered = useMemo(() => {
    if (tab === 'all') return jobs;
    if (tab === 'bidding') return jobs.filter(j => j.type === 'bidding');
    if (tab === 'active') return jobs.filter(j => j.status === 'active' || j.status === 'pending_payment');
    if (tab === 'completed') return jobs.filter(j => j.status === 'completed');
    return jobs.filter(j => j.status === 'cancelled');
  }, [jobs, tab]);
  // Apply the per-tab sub-filter on top of the base tab filter.
  const filtered = useMemo(() => {
    if (subFilter === 'all') return baseFiltered;
    if (tab === 'all') {
      // sub-filter by job status (active/completed/cancelled) or by type
      if (subFilter === 'active') return baseFiltered.filter(j => j.status === 'active' || j.status === 'pending_payment');
      if (subFilter === 'bidding') return baseFiltered.filter(j => j.type === 'bidding');
      return baseFiltered.filter(j => j.status === subFilter);
    }
    if (tab === 'bidding') {
      // sub-filter by job lifecycle status
      if (subFilter === 'open') return baseFiltered.filter(j => j.status === 'bidding_open');
      if (subFilter === 'awarded') return baseFiltered.filter(j => j.status === 'pending_payment' || j.status === 'active' || j.status === 'completed');
      if (subFilter === 'cancelled') return baseFiltered.filter(j => j.status === 'cancelled');
      return baseFiltered;
    }
    if (tab === 'active') {
      if (subFilter === 'in_transit') return baseFiltered.filter(j => j.status === 'active');
      if (subFilter === 'awaiting_payment') return baseFiltered.filter(j => j.status === 'pending_payment');
      return baseFiltered;
    }
    if (tab === 'completed') {
      if (subFilter === 'rated') return baseFiltered.filter(j => !!j.rating);
      if (subFilter === 'unrated') return baseFiltered.filter(j => !j.rating);
      return baseFiltered;
    }
    return baseFiltered;
  }, [baseFiltered, subFilter, tab]);
  // Offers sub-filter is computed separately because offers aren't in `jobs`.
  const filteredOffers = useMemo(() => {
    if (tab !== 'offers') return offers;
    if (subFilter === 'all') return offers;
    return offers.filter(o => o.status === subFilter);
  }, [offers, tab, subFilter]);
  const counts = useMemo(() => {
    return {
      all: jobs.filter(j => j.type !== 'bidding').length,
      bidding: jobs.filter(j => j.type === 'bidding').length,
      active: jobs.filter(j => j.status === 'active' || j.status === 'pending_payment').length,
      completed: jobs.filter(j => j.status === 'completed').length,
      cancelled: jobs.filter(j => j.status === 'cancelled').length,
      searches: searches.length,
      offers: offers.length
    };
  }, [jobs, searches, offers]);
  const handleOpenJob = (job: JobRecord) => {
    setActiveService(job.serviceId, job.prompt);
    // Bidding jobs that are still open take the user to results to view live bids;
    // bidding jobs that have already progressed open the bid-detail modal.
    if (job.type === 'bidding') {
      if (job.status === 'bidding_open') {
        navigate('/results', {
          state: {
            service: job.serviceId,
            prompt: job.prompt
          }
        });
      } else if (job.status === 'completed') {
        setReceiptTarget(job);
      } else {
        setBidDetailTarget(job);
      }
      return;
    }
    if (job.status === 'completed') {
      setReceiptTarget(job);
      return;
    }
    if (job.status === 'active' || job.status === 'pending_payment') {
      navigate('/tracking', {
        state: {
          service: job.serviceId,
          jobId: job.id
        }
      });
    } else if (job.status === 'cancelled') {
      navigate('/booking', {
        state: {
          service: job.serviceId,
          prompt: job.prompt
        }
      });
    }
  };
  return <div className="min-h-[calc(100vh-5rem)] bg-surface-100 py-10" data-id="element-702">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-703">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-704">
          <div data-id="element-705">
            <div className="inline-flex items-center gap-2 bg-brand-50 text-brand-700 px-3 py-1 rounded-full text-xs font-semibold mb-3" data-id="element-706">
              <Briefcase className="w-3.5 h-3.5" data-id="element-707" />
              My Jobs
            </div>
            <h1 className="text-3xl font-display font-bold text-gray-900" data-id="element-708">
              Activity
            </h1>
            <p className="text-gray-500 mt-1" data-id="element-709">
              Your active bids, jobs in progress, and history.
            </p>
          </div>
          <button onClick={() => navigate('/')} className="px-4 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-semibold flex items-center gap-2 self-start sm:self-auto" data-id="element-710">
            <Sparkles className="w-4 h-4" data-id="element-711" /> Start new request
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1" data-id="element-712">
          {TABS.map(t => {
          const active = tab === t.id;
          const count = counts[t.id];
          return <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all border ${active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 hover:text-gray-900 border-gray-100'}`} data-id="element-713">
                <t.icon className="w-4 h-4" data-id="element-714" />
                {t.label}
                <span className={`text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1.5 inline-flex items-center justify-center ${active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-715">
                  {count}
                </span>
              </button>;
        })}
        </div>

        {/* Per-tab sub-filter row */}
        <SubFilterRow tab={tab} jobs={jobs} offers={offers} current={subFilter} onChange={setSubFilter} data-id="element-716" />

        {/* List */}
        {tab === 'searches' ? searches.length === 0 ? <EmptyState tab={tab} onCta={() => navigate('/')} data-id="element-717" /> : <SearchesList searches={searches} onResume={s => {
        setActiveService(s.serviceId, s.prompt);
        navigate('/booking', {
          state: {
            service: s.serviceId,
            prompt: s.prompt
          }
        });
      }} onClear={() => clearSearches()} data-id="element-718" /> : tab === 'offers' ? offers.length === 0 ? <EmptyState tab={tab} onCta={() => navigate('/')} data-id="element-719" /> : filteredOffers.length === 0 ? <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center" data-id="element-720">
              <p className="text-sm font-semibold text-gray-700 mb-1" data-id="element-721">
                No offers match this filter
              </p>
              <p className="text-xs text-gray-500" data-id="element-722">
                Try switching to a different status above.
              </p>
            </div> : <OffersList offers={filteredOffers} onOpen={o => {
        markOfferRead(o.id);
        setOfferDetail(o);
      }} data-id="element-723" /> : filtered.length === 0 ? <EmptyState tab={tab} onCta={() => navigate('/')} data-id="element-724" /> : <div className="space-y-3" data-id="element-725">
            <AnimatePresence data-id="element-726">
              {filtered.map((job, i) => <motion.div key={job.id} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            delay: i * 0.04
          }} data-id="element-727">
                  <JobCard job={job} onOpen={() => handleOpenJob(job)} onCancel={() => setCancelTarget(job)} onViewBids={() => setBidDetailTarget(job)} onRate={() => setRateTarget(job)} data-id="element-728" />
                </motion.div>)}
            </AnimatePresence>
          </div>}
      </div>

      {/* Cancel modal */}
      <AnimatePresence data-id="element-729">
        {cancelTarget && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCancelTarget(null)} data-id="element-730">
            <motion.div initial={{
          scale: 0.96,
          y: 10
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.96,
          y: 10
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6" data-id="element-731">
              <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4" data-id="element-732">
                <AlertCircle className="w-6 h-6" data-id="element-733" />
              </div>
              <h3 className="text-lg font-display font-bold text-gray-900 mb-1" data-id="element-734">
                Cancel this{' '}
                {cancelTarget.type === 'bidding' ? 'bidding' : 'job'}?
              </h3>
              <p className="text-sm text-gray-500 mb-5" data-id="element-735">
                {cancelTarget.type === 'bidding' ? "We'll notify the carrier network that the request is closed. Pending bids will be marked cancelled." : 'A 20% fee may apply if the driver has been dispatched.'}
              </p>
              <div className="space-y-2 mb-5 bg-gray-50 rounded-2xl p-3" data-id="element-736">
                <div className="flex justify-between text-xs" data-id="element-737">
                  <span className="text-gray-500" data-id="element-738">Service</span>
                  <span className="font-semibold text-gray-900" data-id="element-739">
                    {cancelTarget.serviceName}
                  </span>
                </div>
                <div className="flex justify-between text-xs" data-id="element-740">
                  <span className="text-gray-500" data-id="element-741">Route</span>
                  <span className="font-semibold text-gray-900 truncate ml-2" data-id="element-742">
                    {cancelTarget.routeHeadline}
                  </span>
                </div>
                {cancelTarget.price && <div className="flex justify-between text-xs" data-id="element-743">
                    <span className="text-gray-500" data-id="element-744">Amount</span>
                    <span className="font-semibold text-gray-900" data-id="element-745">
                      AED {cancelTarget.price}
                    </span>
                  </div>}
              </div>
              <div className="flex gap-3" data-id="element-746">
                <button onClick={() => setCancelTarget(null)} className="flex-1 py-3 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold text-sm transition-colors" data-id="element-747">
                  Keep it
                </button>
                <button onClick={() => {
              cancelJob(cancelTarget.id, 'User cancelled');
              setCancelTarget(null);
            }} className="flex-1 py-3 bg-rose-500 hover:bg-rose-600 text-white rounded-xl font-semibold text-sm transition-colors" data-id="element-748">
                  Yes, cancel
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Bidding detail modal */}
      <AnimatePresence data-id="element-749">
        {detailJob && <BiddingDetailModal job={detailJob} onClose={() => setBidDetailTarget(null)} onAccept={idx => {
        const updated = acceptBid(detailJob.id, idx);
        if (!updated) return;
        setBidDetailTarget(null);
        navigate('/payment', {
          state: {
            service: detailJob.serviceId,
            prompt: detailJob.prompt,
            jobId: updated.id,
            supplier: {
              name: updated.supplierName,
              price: updated.price,
              truck: updated.supplierTruck
            }
          }
        });
      }} onReject={idx => rejectBid(detailJob.id, idx)} onCancel={() => {
        cancelJob(detailJob.id, 'User cancelled');
      }} data-id="element-750" />}
      </AnimatePresence>

      {/* Offer detail modal — re-derive from live store data so supplier responses stream in */}
      <AnimatePresence data-id="element-751">
        {offerDetail && (() => {
        const liveOffer = offers.find(o => o.id === offerDetail.id) || offerDetail;
        return <OfferDetailModal offer={liveOffer} onClose={() => setOfferDetail(null)} onAccept={() => {
          acceptOffer(liveOffer.id);
        }} onReject={() => {
          rejectOffer(liveOffer.id);
        }} onCounter={(price, message) => {
          submitUserCounter(liveOffer.id, price, message);
        }} data-id="element-752" />;
      })()}
      </AnimatePresence>

      {/* Rate-job modal */}
      <AnimatePresence data-id="element-753">
        {rateTarget && <RateJobModal job={rateTarget} onClose={() => setRateTarget(null)} data-id="element-754" />}
      </AnimatePresence>

      {/* Receipt modal — shown for completed jobs */}
      <AnimatePresence data-id="element-755">
        {receiptTarget && <ReceiptModal job={receiptTarget} onClose={() => setReceiptTarget(null)} data-id="element-756" />}
      </AnimatePresence>
    </div>;
}
// ---- Bidding detail modal -----------------------------------------------
function BiddingDetailModal({
  job,
  onClose,
  onAccept,
  onReject,
  onCancel
}: {
  job: JobRecord;
  onClose: () => void;
  onAccept: (bidIndex: number) => void;
  onReject: (bidIndex: number) => void;
  onCancel: () => void;
}) {
  const bids = bidsWithLifecycle(job);
  // Group bids by status while preserving their original index for actions.
  const indexed = bids.map((b, i) => ({
    bid: b,
    originalIndex: i
  }));
  const pending = indexed.filter(b => !b.bid.status || b.bid.status === 'pending');
  const accepted = indexed.filter(b => b.bid.status === 'accepted');
  const rejected = indexed.filter(b => b.bid.status === 'rejected');
  const expired = indexed.filter(b => b.bid.status === 'expired');
  const cancelled = indexed.filter(b => b.bid.status === 'cancelled');
  const bestPending = pending.length ? pending.reduce((a, b) => a.bid.price < b.bid.price ? a : b) : null;
  const isOpen = job.status === 'bidding_open';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-id="element-757">
      <motion.div initial={{
      y: 30,
      scale: 0.98
    }} animate={{
      y: 0,
      scale: 1
    }} exit={{
      y: 30,
      scale: 0.98
    }} onClick={e => e.stopPropagation()} className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-2xl w-full max-h-[88vh] flex flex-col overflow-hidden" data-id="element-758">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0" data-id="element-759">
          <div className="flex items-start gap-3 min-w-0" data-id="element-760">
            <div className="w-10 h-10 rounded-2xl bg-gray-900 text-white flex items-center justify-center shrink-0" data-id="element-761">
              <Gavel className="w-5 h-5" data-id="element-762" />
            </div>
            <div className="min-w-0" data-id="element-763">
              <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-764">
                <h3 className="font-bold text-gray-900" data-id="element-765">
                  Bidding • {job.serviceName}
                </h3>
                <StatusPill status={job.status} data-id="element-766" />
              </div>
              <p className="text-xs text-gray-500 truncate" data-id="element-767">
                {job.routeHeadline}
              </p>
              {job.prompt && <p className="text-xs text-gray-400 mt-0.5 italic truncate" data-id="element-768">
                  "{job.prompt}"
                </p>}
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 shrink-0" aria-label="Close" data-id="element-769">
            <X className="w-4 h-4" data-id="element-770" />
          </button>
        </div>

        {/* Photo strip — request attachments */}
        {job.photos && job.photos.length > 0 && <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/50 shrink-0" data-id="element-771">
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2" data-id="element-772">
              Attached photos
            </p>
            <div className="flex gap-2 overflow-x-auto" data-id="element-773">
              {job.photos.map((src, i) => <img key={i} src={src} alt={`Upload ${i + 1}`} className="w-16 h-16 rounded-xl object-cover border border-gray-200 shrink-0" data-id="element-774" />)}
            </div>
          </div>}

        {/* Stats strip */}
        <div className="px-6 py-4 bg-gradient-to-b from-brand-50/40 to-white border-b border-gray-100 grid grid-cols-4 gap-2 shrink-0" data-id="element-775">
          <Stat label="Pending" value={pending.length} color="text-amber-700" data-id="element-776" />
          <Stat label="Accepted" value={accepted.length} color="text-green-700" data-id="element-777" />
          <Stat label="Rejected" value={rejected.length} color="text-rose-700" data-id="element-778" />
          <Stat label="Expired" value={expired.length + cancelled.length} color="text-gray-500" data-id="element-779" />
        </div>

        {/* Bids list */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5" data-id="element-780">
          {pending.length > 0 && <Section title="Live bids" subtitle={isOpen ? 'Accept the best offer or reject any individually.' : 'No longer accepting new offers.'} icon={<Sparkles className="w-4 h-4 text-brand-500" data-id="element-782" />} data-id="element-781">
              {pending.map(({
            bid,
            originalIndex
          }) => <BidRow key={originalIndex} bid={bid} isBest={bestPending?.originalIndex === originalIndex} showActions={isOpen} onAccept={() => onAccept(originalIndex)} onReject={() => onReject(originalIndex)} data-id="element-783" />)}
            </Section>}

          {accepted.length > 0 && <Section title="Accepted" icon={<CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-785" />} data-id="element-784">
              {accepted.map(({
            bid,
            originalIndex
          }) => <BidRow key={originalIndex} bid={bid} showActions={false} data-id="element-786" />)}
            </Section>}

          {rejected.length > 0 && <Section title="Rejected" icon={<XCircle className="w-4 h-4 text-rose-500" data-id="element-788" />} data-id="element-787">
              {rejected.map(({
            bid,
            originalIndex
          }) => <BidRow key={originalIndex} bid={bid} showActions={false} data-id="element-789" />)}
            </Section>}

          {expired.length > 0 && <Section title="Expired" icon={<Hourglass className="w-4 h-4 text-gray-400" data-id="element-791" />} data-id="element-790">
              {expired.map(({
            bid,
            originalIndex
          }) => <BidRow key={originalIndex} bid={bid} showActions={false} data-id="element-792" />)}
            </Section>}

          {cancelled.length > 0 && <Section title="Cancelled" icon={<Ban className="w-4 h-4 text-gray-400" data-id="element-794" />} data-id="element-793">
              {cancelled.map(({
            bid,
            originalIndex
          }) => <BidRow key={originalIndex} bid={bid} showActions={false} data-id="element-795" />)}
            </Section>}

          {bids.length === 0 && <div className="text-center py-10 text-sm text-gray-500" data-id="element-796">
              No bids received yet. Carriers usually respond within minutes —
              come back soon.
            </div>}
        </div>

        {/* Footer actions */}
        {isOpen && <div className="border-t border-gray-100 px-6 py-3 flex justify-between items-center shrink-0 bg-white" data-id="element-797">
            <p className="text-xs text-gray-500" data-id="element-798">
              {pending.length} live bid{pending.length === 1 ? '' : 's'}
            </p>
            <button onClick={onCancel} className="px-4 py-2 bg-white border border-gray-200 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 text-gray-700 rounded-xl text-xs font-semibold transition-colors" data-id="element-799">
              Cancel request
            </button>
          </div>}
      </motion.div>
    </motion.div>;
}
// ---- Bid row -------------------------------------------------------------
function BidRow({
  bid,
  isBest,
  showActions,
  onAccept,
  onReject
}: {
  bid: JobBid;
  isBest?: boolean;
  showActions: boolean;
  onAccept?: () => void;
  onReject?: () => void;
}) {
  const statusStyle: Record<BidStatus, string> = {
    pending: '',
    accepted: 'bg-green-50 border-green-100',
    rejected: 'bg-rose-50/40 border-rose-100 opacity-80',
    expired: 'bg-gray-50 border-gray-100 opacity-70',
    cancelled: 'bg-gray-50 border-gray-100 opacity-70'
  };
  const effective: BidStatus = bid.status || 'pending';
  return <div className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 border transition-colors ${isBest && effective === 'pending' ? 'bg-brand-50/60 border-brand-200' : statusStyle[effective] || 'bg-white border-gray-100'}`} data-id="element-800">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${bid.color}`} data-id="element-801">
        {bid.initials}
      </div>
      <div className="flex-1 min-w-0" data-id="element-802">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-803">
          <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-804">
            {bid.supplier}
          </p>
          {isBest && effective === 'pending' && <span className="text-[9px] font-bold uppercase tracking-wider bg-brand-500 text-white px-1.5 py-0.5 rounded" data-id="element-805">
              Best
            </span>}
          {effective !== 'pending' && <BidStatusBadge status={effective} data-id="element-806" />}
        </div>
        <p className="text-[11px] text-gray-500" data-id="element-807">
          {bid.rating} ★ ·{' '}
          {new Date(bid.receivedAt).toLocaleString('en-AE', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })}
        </p>
      </div>
      <div className="text-right shrink-0" data-id="element-808">
        <p className="font-display font-bold text-gray-900" data-id="element-809">AED {bid.price}</p>
        {showActions && <div className="flex gap-1 mt-1 justify-end" data-id="element-810">
            <button onClick={onReject} className="text-[10px] font-semibold text-gray-500 hover:text-rose-600 px-2 py-0.5 rounded hover:bg-rose-50 transition-colors" data-id="element-811">
              Reject
            </button>
            <button onClick={onAccept} className="text-[10px] font-bold text-white bg-gray-900 hover:bg-black px-2.5 py-0.5 rounded transition-colors" data-id="element-812">
              Accept
            </button>
          </div>}
      </div>
    </div>;
}
function BidStatusBadge({
  status
}: {
  status: BidStatus;
}) {
  const cfg: Record<BidStatus, {
    label: string;
    cls: string;
    icon?: React.ReactNode;
  }> = {
    pending: {
      label: 'Pending',
      cls: 'bg-amber-50 text-amber-700 border-amber-100'
    },
    accepted: {
      label: 'Accepted',
      cls: 'bg-green-50 text-green-700 border-green-100',
      icon: <CheckCircle2 className="w-3 h-3" data-id="element-813" />
    },
    rejected: {
      label: 'Rejected',
      cls: 'bg-rose-50 text-rose-700 border-rose-100',
      icon: <XCircle className="w-3 h-3" data-id="element-814" />
    },
    expired: {
      label: 'Expired',
      cls: 'bg-gray-100 text-gray-600 border-gray-200',
      icon: <Hourglass className="w-3 h-3" data-id="element-815" />
    },
    cancelled: {
      label: 'Cancelled',
      cls: 'bg-gray-100 text-gray-600 border-gray-200',
      icon: <Ban className="w-3 h-3" data-id="element-816" />
    }
  };
  const c = cfg[status];
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${c.cls}`} data-id="element-817">
      {c.icon}
      {c.label}
    </span>;
}
function Section({
  title,
  subtitle,
  icon,
  children
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return <div data-id="element-818">
      <div className="flex items-center gap-2 mb-2" data-id="element-819">
        {icon}
        <h4 className="font-bold text-gray-900 text-sm" data-id="element-820">{title}</h4>
      </div>
      {subtitle && <p className="text-xs text-gray-500 mb-2 -mt-1" data-id="element-821">{subtitle}</p>}
      <div className="space-y-2" data-id="element-822">{children}</div>
    </div>;
}
function Stat({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: string;
}) {
  return <div className="text-center" data-id="element-823">
      <p className={`text-2xl font-display font-bold ${color}`} data-id="element-824">{value}</p>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-825">
        {label}
      </p>
    </div>;
}
// ---- Job card ------------------------------------------------------------
function JobCard({
  job,
  onOpen,
  onCancel,
  onViewBids,
  onRate
}: {
  job: JobRecord;
  onOpen: () => void;
  onCancel: () => void;
  onViewBids: () => void;
  onRate: () => void;
}) {
  const created = new Date(job.createdAt);
  const closesIn = job.closesAt && job.status === 'bidding_open' ? Math.max(0, job.closesAt - Date.now()) : 0;
  const hoursLeft = Math.floor(closesIn / (60 * 60 * 1000));
  const minutesLeft = Math.floor(closesIn % (60 * 60 * 1000) / (60 * 1000));
  // Bid summary for bidding jobs
  const bidStats = job.type === 'bidding' && job.bids ? bidsWithLifecycle(job).reduce((acc, b) => {
    const s = b.status || 'pending';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {} as Record<BidStatus, number>) : null;
  return <div className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all overflow-hidden" data-id="element-826">
      <div className="p-5 flex flex-col sm:flex-row gap-4" data-id="element-827">
        <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-700 flex items-center justify-center shrink-0" data-id="element-828">
          {job.type === 'bidding' ? <Gavel className="w-5 h-5" data-id="element-829" /> : <Truck className="w-5 h-5" data-id="element-830" />}
        </div>

        <div className="flex-1 min-w-0" data-id="element-831">
          <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap" data-id="element-832">
            <div className="min-w-0" data-id="element-833">
              <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-834">
                <h3 className="font-bold text-gray-900 truncate" data-id="element-835">
                  {job.serviceName}
                </h3>
                <StatusPill status={job.status} data-id="element-836" />
                {job.type === 'bidding' && <span className="text-[9px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded-full" data-id="element-837">
                    Bidding
                  </span>}
              </div>
              <p className="text-xs text-gray-500 truncate" data-id="element-838">
                {job.routeHeadline}
              </p>
            </div>
            <div className="text-right shrink-0" data-id="element-839">
              {job.price ? <p className="font-display font-bold text-lg text-gray-900" data-id="element-840">
                  AED {job.price}
                </p> : job.estimatedPrice ? <p className="text-xs text-gray-500" data-id="element-841">
                  Est. AED {job.estimatedPrice}
                </p> : null}
              <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium" data-id="element-842">
                {created.toLocaleDateString('en-AE', {
                day: '2-digit',
                month: 'short'
              })}
              </p>
            </div>
          </div>

          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500" data-id="element-843">
            {job.supplierName && <span className="flex items-center gap-1" data-id="element-844">
                <Truck className="w-3.5 h-3.5" data-id="element-845" />
                <span className="font-medium text-gray-700" data-id="element-846">
                  {job.supplierName}
                </span>
              </span>}
            {job.status === 'bidding_open' && <span className="flex items-center gap-1" data-id="element-847">
                <Clock className="w-3.5 h-3.5" data-id="element-848" />
                Closes in {hoursLeft}h {minutesLeft}m
              </span>}
            {job.pickup && <span className="flex items-center gap-1 min-w-0 truncate" data-id="element-849">
                <MapPin className="w-3.5 h-3.5 shrink-0" data-id="element-850" />
                <span className="truncate" data-id="element-851">
                  {job.pickup} → {job.dropoff}
                </span>
              </span>}
          </div>

          {/* Rating display — completed & rated jobs */}
          {job.status === 'completed' && job.rating && <div className="mt-3 p-3 rounded-2xl bg-amber-50/60 border border-amber-100" data-id="element-852">
              <div className="flex items-center gap-2 mb-1.5" data-id="element-853">
                <div className="flex gap-0.5" data-id="element-854">
                  {[1, 2, 3, 4, 5].map(n => <Star key={n} className={`w-4 h-4 ${n <= (job.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} strokeWidth={1.5} data-id="element-855" />)}
                </div>
                <span className="text-xs font-bold text-amber-700" data-id="element-856">
                  {job.rating}.0
                </span>
                <span className="text-[10px] text-gray-400 font-medium" data-id="element-857">
                  · You rated this
                </span>
              </div>
              {job.ratingTags && job.ratingTags.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-858">
                  {job.ratingTags.map(t => <span key={t} className="text-[10px] font-semibold bg-white text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full" data-id="element-859">
                      {t}
                    </span>)}
                </div>}
              {job.reviewText && <p className="text-xs text-gray-700 mt-2 italic leading-relaxed line-clamp-2" data-id="element-860">
                  "{job.reviewText}"
                </p>}
            </div>}

          {/* Rating prompt — completed & unrated */}
          {job.status === 'completed' && !job.rating && <button onClick={e => {
          e.stopPropagation();
          onRate();
        }} className="mt-3 w-full sm:w-auto group flex items-center gap-2 p-3 pr-4 rounded-2xl bg-gradient-to-r from-amber-50 to-white border-2 border-amber-200 hover:border-amber-300 hover:shadow-sm transition-all" data-id="element-861">
              <div className="w-9 h-9 rounded-xl bg-amber-400 text-white flex items-center justify-center group-hover:scale-105 transition-transform" data-id="element-862">
                <Star className="w-4 h-4 fill-current" data-id="element-863" />
              </div>
              <div className="text-left flex-1 min-w-0" data-id="element-864">
                <p className="text-xs font-bold text-gray-900" data-id="element-865">
                  Rate your move with {job.supplierName}
                </p>
                <p className="text-[10px] text-amber-700 font-medium" data-id="element-866">
                  Takes 10 seconds · helps other movers
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-amber-600 shrink-0" data-id="element-867" />
            </button>}

          {/* Photos preview — if the user attached photos */}
          {job.photos && job.photos.length > 0 && <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-id="element-868">
              <div className="flex -space-x-2" data-id="element-869">
                {job.photos.slice(0, 4).map((src, i) => <img key={i} src={src} alt={`Upload ${i + 1}`} className="w-10 h-10 rounded-lg object-cover border-2 border-white shadow-sm" data-id="element-870" />)}
              </div>
              <span className="text-[10px] text-gray-500 font-semibold ml-1" data-id="element-871">
                {job.photos.length} photo{job.photos.length === 1 ? '' : 's'}{' '}
                attached
              </span>
            </div>}

          {/* Bid status summary — bidding jobs only */}
          {bidStats && <div className="flex flex-wrap gap-1.5 mt-3" data-id="element-872">
              {(['pending', 'accepted', 'rejected', 'expired', 'cancelled'] as BidStatus[]).map(s => bidStats[s] ? <BidCountChip key={s} status={s} count={bidStats[s]} data-id="element-873" /> : null)}
            </div>}

          {/* Actions */}
          <div className="flex gap-2 mt-4 flex-wrap" data-id="element-874">
            {job.type === 'bidding' && (job.bids?.length || 0) > 0 && <button onClick={onViewBids} className="px-4 py-2 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-semibold transition-colors flex items-center gap-1" data-id="element-875">
                View bids ({job.bids!.length}){' '}
                <ChevronRight className="w-3.5 h-3.5" data-id="element-876" />
              </button>}
            {(job.type !== 'bidding' || (job.bids?.length || 0) === 0) && <button onClick={onOpen} className="px-4 py-2 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-semibold transition-colors flex items-center gap-1" data-id="element-877">
                {primaryActionLabel(job)}{' '}
                <ChevronRight className="w-3.5 h-3.5" data-id="element-878" />
              </button>}
            {job.type === 'bidding' && (job.bids?.length || 0) > 0 && job.status !== 'bidding_open' && <button onClick={onOpen} className="px-4 py-2 bg-white border border-gray-200 hover:border-brand-300 hover:bg-brand-50 text-gray-700 rounded-xl text-xs font-semibold transition-colors" data-id="element-879">
                  {primaryActionLabel(job)}
                </button>}
            {isCancellable(job.status) && <button onClick={onCancel} className="px-4 py-2 bg-white border border-gray-200 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 text-gray-700 rounded-xl text-xs font-semibold transition-colors" data-id="element-880">
                Cancel
              </button>}
            {job.status === 'cancelled' && <button onClick={onOpen} className="px-4 py-2 bg-white border border-gray-200 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 text-gray-700 rounded-xl text-xs font-semibold transition-colors" data-id="element-881">
                Rebook
              </button>}
          </div>
        </div>
      </div>
    </div>;
}
function BidCountChip({
  status,
  count
}: {
  status: BidStatus;
  count: number;
}) {
  const cfg: Record<BidStatus, {
    label: string;
    cls: string;
  }> = {
    pending: {
      label: 'pending',
      cls: 'bg-amber-50 text-amber-700 border-amber-100'
    },
    accepted: {
      label: 'accepted',
      cls: 'bg-green-50 text-green-700 border-green-100'
    },
    rejected: {
      label: 'rejected',
      cls: 'bg-rose-50 text-rose-700 border-rose-100'
    },
    expired: {
      label: 'expired',
      cls: 'bg-gray-100 text-gray-600 border-gray-200'
    },
    cancelled: {
      label: 'cancelled',
      cls: 'bg-gray-100 text-gray-600 border-gray-200'
    }
  };
  const c = cfg[status];
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${c.cls}`} data-id="element-882">
      <span className="font-bold" data-id="element-883">{count}</span> {c.label}
    </span>;
}
function StatusPill({
  status
}: {
  status: JobStatus;
}) {
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusColor(status)}`} data-id="element-884">
      {(status === 'bidding_open' || status === 'active') && <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${status === 'bidding_open' ? 'bg-amber-500' : 'bg-green-500'}`} data-id="element-885" />}
      {statusLabel(status)}
    </span>;
}
function primaryActionLabel(job: JobRecord): string {
  switch (job.status) {
    case 'bidding_open':
      return 'View bids';
    case 'bidding_closed':
      return 'View';
    case 'pending_payment':
      return 'Complete payment';
    case 'active':
      return 'Track live';
    case 'completed':
      return 'View receipt';
    case 'cancelled':
      return 'View';
  }
}
function EmptyState({
  tab,
  onCta
}: {
  tab: Tab;
  onCta: () => void;
}) {
  const messages: Record<Tab, {
    title: string;
    desc: string;
    cta: string;
  }> = {
    all: {
      title: "You haven't booked anything yet",
      desc: 'Start a new request and your jobs will live here.',
      cta: 'Start a request'
    },
    bidding: {
      title: 'No bidding requests yet',
      desc: 'Open a request for bidding to let carriers compete.',
      cta: 'Open a bidding request'
    },
    active: {
      title: 'No active jobs',
      desc: 'Booked jobs in progress will show up here.',
      cta: 'Book a new job'
    },
    completed: {
      title: 'Nothing completed yet',
      desc: 'Your finished jobs will appear here with receipts.',
      cta: 'Start your first job'
    },
    cancelled: {
      title: 'No cancellations',
      desc: 'Jobs you cancel will be listed here.',
      cta: 'Browse marketplace'
    },
    searches: {
      title: 'No searches yet',
      desc: 'Every prompt you start a booking with shows up here so you can resume later.',
      cta: 'Start a new search'
    },
    offers: {
      title: 'No counter offers yet',
      desc: 'Use "Request Better Offer" on the results page to negotiate with a specific carrier — their counter lands here.',
      cta: 'Browse carriers'
    }
  };
  const m = messages[tab];
  return <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-10 text-center" data-id="element-886">
      <div className="w-14 h-14 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center mx-auto mb-4" data-id="element-887">
        <Briefcase className="w-6 h-6" data-id="element-888" />
      </div>
      <h3 className="font-bold text-gray-900 mb-1" data-id="element-889">{m.title}</h3>
      <p className="text-sm text-gray-500 mb-5" data-id="element-890">{m.desc}</p>
      <button onClick={onCta} className="px-5 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-sm font-semibold inline-flex items-center gap-2" data-id="element-891">
        <Sparkles className="w-4 h-4" data-id="element-892" /> {m.cta}
      </button>
    </div>;
}
function SearchesList({
  searches,
  onResume,
  onClear
}: {
  searches: SearchRecord[];
  onResume: (s: SearchRecord) => void;
  onClear: () => void;
}) {
  return <div className="space-y-3" data-id="element-893">
      <div className="flex items-center justify-between px-1 mb-1" data-id="element-894">
        <p className="text-xs text-gray-500" data-id="element-895">
          <span className="font-semibold text-gray-700" data-id="element-896">{searches.length}</span>{' '}
          historical search{searches.length === 1 ? '' : 'es'} — resume any to
          jump back into the AI booking flow
        </p>
        <button onClick={onClear} className="text-xs font-semibold text-gray-500 hover:text-rose-600 inline-flex items-center gap-1 transition-colors" data-id="element-897">
          <Trash2 className="w-3.5 h-3.5" data-id="element-898" />
          Clear all
        </button>
      </div>
      <AnimatePresence data-id="element-899">
        {searches.map((s, i) => <motion.div key={s.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        delay: i * 0.04
      }} data-id="element-900">
            <button onClick={() => onResume(s)} className="w-full text-left bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-md hover:border-brand-200 transition-all p-5 flex items-center gap-4 group" data-id="element-901">
              <div className="w-11 h-11 rounded-2xl orange-gradient-bg flex items-center justify-center shrink-0 shadow-sm" data-id="element-902">
                <Sparkles className="w-5 h-5 text-white" data-id="element-903" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-904">
                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-905">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 text-[10px] font-bold uppercase tracking-wider" data-id="element-906">
                    {s.serviceName}
                  </span>
                  <span className="text-[10px] text-gray-400 font-medium" data-id="element-907">
                    {timeAgo(s.createdAt)}
                  </span>
                </div>
                <p className="font-medium text-gray-900 truncate group-hover:text-brand-700 transition-colors" data-id="element-908">
                  "{s.prompt}"
                </p>
              </div>
              <div className="shrink-0 flex items-center gap-2 text-brand-600 font-semibold text-sm" data-id="element-909">
                <span className="hidden sm:inline" data-id="element-910">Resume</span>
                <ArrowRight className="w-4 h-4" data-id="element-911" />
              </div>
            </button>
          </motion.div>)}
      </AnimatePresence>
    </div>;
}
function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr${hr === 1 ? '' : 's'} ago`;
  const days = Math.floor(hr / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
function OffersList({
  offers,
  onOpen
}: {
  offers: OfferRecord[];
  onOpen: (o: OfferRecord) => void;
}) {
  return <div className="space-y-3" data-id="element-912">
      <p className="text-xs text-gray-500 px-1 mb-1" data-id="element-913">
        <span className="font-semibold text-gray-700" data-id="element-914">{offers.length}</span>{' '}
        counter offer
        {offers.length === 1 ? '' : 's'} — accept, counter back, or reject.
      </p>
      <AnimatePresence data-id="element-915">
        {offers.map((o, i) => <motion.div key={o.id} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        delay: i * 0.04
      }} data-id="element-916">
            <button onClick={() => onOpen(o)} className={`w-full text-left bg-white rounded-3xl border shadow-sm hover:shadow-md transition-all p-5 flex items-center gap-4 ${o.unread ? 'border-brand-300 ring-2 ring-brand-100' : 'border-gray-100 hover:border-gray-200'}`} data-id="element-917">
              <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-700 flex items-center justify-center shrink-0 relative" data-id="element-918">
                <TrendingDown className="w-5 h-5" data-id="element-919" />
                {o.unread && <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-brand-500 border-2 border-white animate-pulse" data-id="element-920" />}
              </div>
              <div className="flex-1 min-w-0" data-id="element-921">
                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-922">
                  <p className="font-bold text-gray-900 truncate" data-id="element-923">
                    {o.supplierName}
                  </p>
                  <OfferStatusPill status={o.status} side={o.currentSide} data-id="element-924" />
                </div>
                <p className="text-xs text-gray-500 truncate" data-id="element-925">
                  {o.serviceName} · {o.routeHeadline}
                </p>
                {o.history.length > 0 && <p className="text-xs text-gray-400 mt-1 truncate italic" data-id="element-926">
                    {o.currentSide === 'supplier' ? `Carrier: ` : `You: `}"
                    {o.history[o.history.length - 1].message || (o.currentSide === 'supplier' ? 'Sent a counter.' : 'Proposed price.')}
                    "
                  </p>}
              </div>
              <div className="text-right shrink-0" data-id="element-927">
                <p className="text-[10px] text-gray-400 line-through font-medium" data-id="element-928">
                  AED {o.originalPrice}
                </p>
                <p className="font-display font-bold text-lg text-gray-900" data-id="element-929">
                  AED {o.currentPrice}
                </p>
                <p className="text-[10px] font-bold text-green-700" data-id="element-930">
                  –AED {o.originalPrice - o.currentPrice}
                </p>
              </div>
            </button>
          </motion.div>)}
      </AnimatePresence>
    </div>;
}
function OfferStatusPill({
  status,
  side
}: {
  status: OfferStatus;
  side: 'user' | 'supplier';
}) {
  const cfg: Record<OfferStatus, {
    label: string;
    cls: string;
  }> = {
    pending: {
      label: side === 'user' ? 'Awaiting carrier' : 'Awaiting you',
      cls: 'bg-amber-50 text-amber-700 border-amber-100'
    },
    countered: {
      label: side === 'supplier' ? 'Carrier countered' : 'You countered',
      cls: 'bg-blue-50 text-blue-700 border-blue-100'
    },
    accepted: {
      label: 'Accepted',
      cls: 'bg-green-50 text-green-700 border-green-100'
    },
    rejected: {
      label: 'Rejected',
      cls: 'bg-rose-50 text-rose-700 border-rose-100'
    },
    expired: {
      label: 'Expired',
      cls: 'bg-gray-100 text-gray-600 border-gray-200'
    }
  };
  const c = cfg[status];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${c.cls}`} data-id="element-931">
      {c.label}
    </span>;
}
function OfferDetailModal({
  offer,
  onClose,
  onAccept,
  onReject,
  onCounter
}: {
  offer: OfferRecord;
  onClose: () => void;
  onAccept: () => void;
  onReject: () => void;
  onCounter: (price: number, message?: string) => void;
}) {
  const [composeOpen, setComposeOpen] = useState(false);
  const initialCounter = Math.round((offer.currentPrice + offer.originalPrice * 0.92) / 2);
  const [counterPrice, setCounterPrice] = useState(initialCounter);
  const [counterMessage, setCounterMessage] = useState('');
  const canAct = offer.status !== 'accepted' && offer.status !== 'rejected' && offer.status !== 'expired';
  const supplierToAct = offer.currentSide === 'supplier'; // supplier proposed last, user can act
  const minCounter = offer.currentPrice;
  const maxCounter = offer.originalPrice;
  const savings = offer.originalPrice - offer.currentPrice;
  const savingsPct = Math.round(savings / offer.originalPrice * 100);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-id="element-932">
      <motion.div initial={{
      y: 30,
      scale: 0.98
    }} animate={{
      y: 0,
      scale: 1
    }} exit={{
      y: 30,
      scale: 0.98
    }} onClick={e => e.stopPropagation()} className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-xl w-full max-h-[88vh] flex flex-col overflow-hidden" data-id="element-933">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0" data-id="element-934">
          <div className="flex items-center gap-3 min-w-0" data-id="element-935">
            <div className="w-10 h-10 rounded-2xl bg-gray-900 text-white flex items-center justify-center shrink-0" data-id="element-936">
              <TrendingDown className="w-5 h-5" data-id="element-937" />
            </div>
            <div className="min-w-0" data-id="element-938">
              <div className="flex items-center gap-2 flex-wrap" data-id="element-939">
                <h3 className="font-bold text-gray-900 truncate" data-id="element-940">
                  {offer.supplierName}
                </h3>
                <OfferStatusPill status={offer.status} side={offer.currentSide} data-id="element-941" />
              </div>
              <p className="text-xs text-gray-500 truncate" data-id="element-942">
                {offer.serviceName} · {offer.routeHeadline}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 shrink-0" data-id="element-943">
            <X className="w-4 h-4" data-id="element-944" />
          </button>
        </div>

        {/* Stats strip */}
        <div className="px-6 py-3 bg-gradient-to-b from-brand-50/40 to-white border-b border-gray-100 grid grid-cols-3 gap-2 shrink-0" data-id="element-945">
          <div className="text-center" data-id="element-946">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-947">
              Original
            </p>
            <p className="font-display font-bold text-gray-900 line-through" data-id="element-948">
              AED {offer.originalPrice}
            </p>
          </div>
          <div className="text-center" data-id="element-949">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-950">
              Current
            </p>
            <p className="font-display font-bold text-gray-900" data-id="element-951">
              AED {offer.currentPrice}
            </p>
          </div>
          <div className="text-center" data-id="element-952">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500" data-id="element-953">
              You save
            </p>
            <p className="font-display font-bold text-green-700" data-id="element-954">
              AED {savings}
              <span className="text-xs ml-1 font-semibold" data-id="element-955">
                ({savingsPct}%)
              </span>
            </p>
          </div>
        </div>

        {/* Conversation thread */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3 bg-gray-50/50" data-id="element-956">
          {offer.history.map((turn, i) => {
          const isUser = turn.by === 'user';
          return <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-id="element-957">
                <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${isUser ? 'bg-gray-900 text-white rounded-br-md' : 'bg-white border border-gray-100 text-gray-900 rounded-bl-md shadow-sm'}`} data-id="element-958">
                  <div className="flex items-center gap-2 mb-1" data-id="element-959">
                    <span className={`text-[10px] uppercase tracking-wider font-bold ${isUser ? 'text-white/60' : 'text-gray-500'}`} data-id="element-960">
                      {isUser ? 'You' : offer.supplierName}
                    </span>
                    <span className={`font-display font-bold ${isUser ? 'text-white' : 'text-gray-900'}`} data-id="element-961">
                      AED {turn.price}
                    </span>
                  </div>
                  {turn.message && <p className="text-xs leading-relaxed" data-id="element-962">{turn.message}</p>}
                  <p className={`text-[10px] mt-1 ${isUser ? 'text-white/50' : 'text-gray-400'}`} data-id="element-963">
                    {new Date(turn.at).toLocaleString('en-AE', {
                  hour: '2-digit',
                  minute: '2-digit',
                  day: '2-digit',
                  month: 'short'
                })}
                  </p>
                </div>
              </div>;
        })}
          {offer.status === 'pending' && offer.currentSide === 'user' && <div className="flex justify-start" data-id="element-964">
              <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-md px-4 py-2.5 flex items-center gap-2 shadow-sm" data-id="element-965">
                <div className="flex gap-1" data-id="element-966">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse" data-id="element-967" />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse [animation-delay:0.2s]" data-id="element-968" />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-pulse [animation-delay:0.4s]" data-id="element-969" />
                </div>
                <span className="text-xs text-gray-500" data-id="element-970">
                  {offer.supplierName} is reviewing your offer…
                </span>
              </div>
            </div>}
        </div>

        {/* Counter composer */}
        {composeOpen && canAct && supplierToAct && <div className="px-6 py-4 border-t border-gray-100 bg-white shrink-0 space-y-3" data-id="element-971">
            <div className="flex items-end justify-between" data-id="element-972">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500" data-id="element-973">
                Your counter
              </p>
              <p className="text-2xl font-display font-bold text-gray-900" data-id="element-974">
                AED {counterPrice}
              </p>
            </div>
            <input type="range" min={minCounter} max={maxCounter} step={5} value={counterPrice} onChange={e => setCounterPrice(parseInt(e.target.value))} className="w-full accent-brand-500" data-id="element-975" />
            <textarea value={counterMessage} onChange={e => setCounterMessage(e.target.value)} placeholder="Optional note…" rows={2} className="w-full px-3 py-2 rounded-xl border border-gray-200 outline-none text-sm focus:border-brand-300 resize-none" data-id="element-976" />
            <div className="flex gap-2" data-id="element-977">
              <button onClick={() => setComposeOpen(false)} className="flex-1 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold text-sm" data-id="element-978">
                Cancel
              </button>
              <button onClick={() => {
            onCounter(counterPrice, counterMessage.trim() || undefined);
            setComposeOpen(false);
            setCounterMessage('');
          }} className="flex-1 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl font-semibold text-sm" data-id="element-979">
                Send counter
              </button>
            </div>
          </div>}

        {/* Action bar */}
        {canAct && !composeOpen && <div className="px-6 py-4 border-t border-gray-100 bg-white shrink-0 flex gap-2" data-id="element-980">
            {supplierToAct ? <>
                <button onClick={onReject} className="flex-1 py-3 bg-white border border-gray-200 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 text-gray-700 rounded-xl font-semibold text-sm transition-colors" data-id="element-981">
                  Reject
                </button>
                <button onClick={() => setComposeOpen(true)} className="flex-1 py-3 bg-white border-2 border-gray-200 hover:border-gray-300 text-gray-700 rounded-xl font-semibold text-sm transition-colors" data-id="element-982">
                  Counter back
                </button>
                <button onClick={onAccept} className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-bold text-sm transition-colors shadow-md shadow-green-500/20 flex items-center justify-center gap-1.5" data-id="element-983">
                  <CheckCircle2 className="w-4 h-4" data-id="element-984" /> Accept AED{' '}
                  {offer.currentPrice}
                </button>
              </> : <div className="flex-1 text-center text-xs text-gray-500 py-3" data-id="element-985">
                Waiting on {offer.supplierName} to respond. We'll notify you
                here.
              </div>}
          </div>}

        {(offer.status === 'accepted' || offer.status === 'rejected' || offer.status === 'expired') && <div className="px-6 py-4 border-t border-gray-100 bg-white shrink-0 text-center" data-id="element-986">
            <p className="text-sm text-gray-500" data-id="element-987">
              {offer.status === 'accepted' && '✓ Offer accepted — head to checkout to confirm the booking.'}
              {offer.status === 'rejected' && 'This offer was rejected.'}
              {offer.status === 'expired' && 'This offer has expired.'}
            </p>
          </div>}
      </motion.div>
    </motion.div>;
}
interface SubFilterRowProps {
  tab: string;
  jobs: JobRecord[];
  offers: OfferRecord[];
  current: string;
  onChange: (v: string) => void;
}
function SubFilterRow({
  tab,
  jobs,
  offers,
  current,
  onChange
}: SubFilterRowProps) {
  // Build the per-tab filter options with live counts. Returns null for tabs
  // that don't have meaningful sub-filters (e.g. cancelled, searches).
  let options: {
    id: string;
    label: string;
    count: number;
  }[] = [];
  if (tab === 'all') {
    options = [{
      id: 'all',
      label: 'All',
      count: jobs.length
    }, {
      id: 'active',
      label: 'Active',
      count: jobs.filter(j => j.status === 'active' || j.status === 'pending_payment').length
    }, {
      id: 'completed',
      label: 'Completed',
      count: jobs.filter(j => j.status === 'completed').length
    }, {
      id: 'cancelled',
      label: 'Cancelled',
      count: jobs.filter(j => j.status === 'cancelled').length
    }];
  } else if (tab === 'bidding') {
    const bidding = jobs.filter(j => j.type === 'bidding');
    options = [{
      id: 'all',
      label: 'All bids',
      count: bidding.length
    }, {
      id: 'open',
      label: 'Open',
      count: bidding.filter(j => j.status === 'bidding_open').length
    }, {
      id: 'awarded',
      label: 'Awarded',
      count: bidding.filter(j => j.status === 'pending_payment' || j.status === 'active' || j.status === 'completed').length
    }, {
      id: 'cancelled',
      label: 'Cancelled',
      count: bidding.filter(j => j.status === 'cancelled').length
    }];
  } else if (tab === 'active') {
    const active = jobs.filter(j => j.status === 'active' || j.status === 'pending_payment');
    options = [{
      id: 'all',
      label: 'All',
      count: active.length
    }, {
      id: 'in_transit',
      label: 'In transit',
      count: active.filter(j => j.status === 'active').length
    }, {
      id: 'awaiting_payment',
      label: 'Awaiting payment',
      count: active.filter(j => j.status === 'pending_payment').length
    }];
  } else if (tab === 'completed') {
    const completed = jobs.filter(j => j.status === 'completed');
    options = [{
      id: 'all',
      label: 'All',
      count: completed.length
    }, {
      id: 'rated',
      label: 'Rated',
      count: completed.filter(j => !!j.rating).length
    }, {
      id: 'unrated',
      label: 'Not yet rated',
      count: completed.filter(j => !j.rating).length
    }];
  } else if (tab === 'offers') {
    options = [{
      id: 'all',
      label: 'All',
      count: offers.length
    }, {
      id: 'pending',
      label: 'Awaiting reply',
      count: offers.filter(o => o.status === 'pending').length
    }, {
      id: 'countered',
      label: 'Countered',
      count: offers.filter(o => o.status === 'countered').length
    }, {
      id: 'accepted',
      label: 'Accepted',
      count: offers.filter(o => o.status === 'accepted').length
    }, {
      id: 'rejected',
      label: 'Rejected',
      count: offers.filter(o => o.status === 'rejected').length
    }];
  }
  if (options.length === 0) return null;
  return <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1" data-id="element-988">
      {options.map(o => {
      const active = current === o.id;
      const dim = o.count === 0 && o.id !== 'all';
      return <button key={o.id} onClick={() => onChange(o.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${active ? 'bg-brand-50 text-brand-700 border-brand-200' : 'bg-white text-gray-600 hover:text-gray-900 border-gray-100'} ${dim ? 'opacity-50' : ''}`} data-id="element-989">
            {o.label}
            <span className={`text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1.5 inline-flex items-center justify-center ${active ? 'bg-white text-brand-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-990">
              {o.count}
            </span>
          </button>;
    })}
    </div>;
}